import React, { FC, useEffect, useState } from 'react';
import { SCUIButton, SCUINoRecordComponent, SCUILoadingLinear } from '../../atomic/atoms';
import { SCUIContent } from '../../atomic/organisms';
import { useTranslation } from 'react-i18next';
import { SCUIBreadCrumb } from '../../atomic/molecules';
import { Grid, FormControl, MenuItem, Select, InputLabel, Box } from '@mui/material';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import Swal from 'sweetalert2';
import DeliveryListTable from './components/DeliveryListTable';
import EditDeliveryDrawer from './components/EditDeliveryDrawer';
import AddDeliveryDrawer from './components/AddDeliveryDrawer';
import TabContainer from './components/TabContainer';
import BulkUploadTab from './components/BulkUploadTab';
import SCUIDownloadButton from '../../atomic/atoms/SCUIDownloadButton/SCUIDownloadButton';
import FastDeliveryCargoManagementCommand from '../../services/command/fastDeliveryCargoManagement/FastDeliveryCargoManagementCommand';
import { FastDeliveryCargoManagementQuery } from '../../services/query';
import { SCUICustomDataGrid } from '../../atomic/molecules';
import { useLocation, useHistory } from 'react-router-dom';
import { FDCM } from './fastDeliveryCargoManagement.classes';

interface Company {
    value: number;
    text: string;
}

export const FastDeliveryCargoManagement: FC<any> = () => {
    const location = useLocation();
    const history = useHistory();
    const [companyList, setCompanyList] = useState<Company[]>([]);
    const [gridData, setGridData] = useState<any>([]);
    const [excelHiddenColumns, setExcelHiddenColumns] = useState<any>([]);
    const [loading, setLoading] = useState(false);
    const [editDeliveryDrawerOpen, setEditDeliveryDrawerOpen] = useState(false);
    const [addDeliveryDrawerOpen, setAddDeliveryDrawerOpen] = useState(false);
    const [editDeliveryRow, setEditDeliveryRow] = useState<any>(null);
    const [selectedTab, setSelectedTab] = useState(0);
    const [bulkUploadResult, setBulkUploadResult] = useState<any[]>([]);

    const { t } = useTranslation();

    const getInitialCompanyId = () => {
        const params = new URLSearchParams(location.search);
        return Number(params.get('companyId')) || 0;
    };
    const [companyId, setCompanyId] = useState(getInitialCompanyId());

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (companyId) {
            params.set('companyId', String(companyId));
        } else {
            params.delete('companyId');
        }
        history.replace({ search: params.toString() });
    }, [companyId]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const urlCompanyId = Number(params.get('companyId'));
        if (urlCompanyId && urlCompanyId !== companyId) {
            setCompanyId(urlCompanyId);
            handleFilter();
        }
    }, []);

    const columns: ExtendedGridColDef[] = [
        {
            field: 'shippingCompanyName',
            headerName: t('fastDeliveryCargoManagement.shippingCompany'),
            flex: 1,
            minWidth: 150,
        },
        {
            field: 'cityName',
            headerName: t('fastDeliveryCargoManagement.city'),
            flex: 1,
            minWidth: 120,
        },
        {
            field: 'countyName',
            headerName: t('fastDeliveryCargoManagement.district'),
            flex: 1,
            minWidth: 120,
        },
        {
            field: 'neighborhoodName',
            headerName: t('fastDeliveryCargoManagement.neighborhood'),
            flex: 1,
            minWidth: 120,
        },
        {
            field: 'isActiveForTomorrowDelivery',
            headerName: t('fastDeliveryCargoManagement.tomorrowDelivery'),
            flex: 1,
            minWidth: 120,
            type: 'boolean',
            renderCell: (params) => (
                <Box sx={{ color: params.value ? 'success.main' : 'error.main' }}>
                    {params.value ? t('general.yes') : t('general.no')}
                </Box>
            ),
        },
        {
            field: 'isActiveForExpressDelivery',
            headerName: t('fastDeliveryCargoManagement.expressDelivery'),
            flex: 1,
            minWidth: 120,
            type: 'boolean',
            renderCell: (params) => (
                <Box sx={{ color: params.value ? 'success.main' : 'error.main' }}>
                    {params.value ? t('general.yes') : t('general.no')}
                </Box>
            ),
        },
        {
            field: 'isActiveForTodayDelivery',
            headerName: t('fastDeliveryCargoManagement.todayDelivery'),
            flex: 1,
            minWidth: 120,
            type: 'boolean',
            renderCell: (params) => (
                <Box sx={{ color: params.value ? 'success.main' : 'error.main' }}>
                    {params.value ? t('general.yes') : t('general.no')}
                </Box>
            ),
        },
        {
            field: 'actions',
            headerName: t('general.edit'),
            flex: 1,
            minWidth: 100,
            renderCell: (params) => (
                <SCUIButton
                    className={FDCM.inputButtonEditRow}
                    text={t('fastDeliveryCargoManagement.edit')}
                    type="primary"
                    sizeType="default"
                    size="s"
                    onClick={() => handleEdit(params.row)}
                />
            ),
        },
    ];

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setSelectedTab(newValue);
    };

    const handleSave = async (data: any) => {
        try {
            const response = await FastDeliveryCargoManagementCommand.UpdateFastDeliveryCargo({
                shippingCompanyId: data.shippingCompanyId,
                addressEntityId: data.addressEntityId,
                isActiveForTomorrowDelivery: data.isActiveForTomorrowDelivery,
                isActiveForTodayDelivery: data.isActiveForTodayDelivery,
                isActiveForExpressDelivery: data.isActiveForExpressDelivery,
            });
            if (response?.status) {
                Swal.fire({
                    title: t('general.success'),
                    text: response?.message || t('general.success'),
                    icon: 'success',
                });
                setEditDeliveryDrawerOpen(false);
                handleFilter();
            } else {
                Swal.fire({
                    title: t('general.error'),
                    text: response.data?.message || t('general.error'),
                    icon: 'error',
                });
            }
        } catch (error: any) {
            Swal.fire({
                title: t('general.error'),
                text: error.toString(),
                icon: 'error',
            });
        }
    };

    const handleAdd = async (data: any) => {
        setLoading(true);
        try {
            const requestData = {
                shippingCompanyId: Number(data.shippingCompanyId),
                addressEntityId: data.neighborhoodId,
                isActiveForTomorrowDelivery: data.isActiveForTomorrowDelivery,
                isActiveForExpressDelivery: data.isActiveForExpressDelivery,
                isActiveForTodayDelivery: data.isActiveForTodayDelivery,
            };

            const response = await FastDeliveryCargoManagementCommand.AddFastDeliveryCargo(requestData);
            if (response) {
                await handleFilter();
                setAddDeliveryDrawerOpen(false);
                Swal.fire({
                    icon: 'success',
                    title: t('fastDeliveryCargoManagement.addSuccess'),
                    showConfirmButton: false,
                    timer: 1500,
                });
            }
        } catch (error: any) {
            Swal.fire({
                icon: 'error',
                title: t('fastDeliveryCargoManagement.addError'),
                text: error.message,
            });
        } finally {
            setLoading(false);
        }
    };

    const GetCargoCompanyList = async () => {
        try {
            setLoading(true);
            const response = await FastDeliveryCargoManagementQuery.GetShippingCompanyForFastCargo().then(
                (res: any) => {
                    return res;
                }
            );
            if (response.data) {
                const formattedList = [
                    { value: 0, text: t('general.all') },
                    ...response.data.map((item: any) => ({
                        value: item.id,
                        text: item.name,
                    })),
                ];
                setCompanyList(formattedList);
            } else {
                Swal.fire('Service Error', response.errorMessage, 'error');
            }
        } catch (error: any) {
            Swal.fire('Error', error.toString(), 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (row: any) => {
        setEditDeliveryRow({
            id: row.id,
            shippingCompanyId: row.shippingCompanyId,
            shippingCompanyName: row.shippingCompanyName,
            cityName: row.cityName,
            countyName: row.countyName,
            neighborhoodName: row.neighborhoodName,
            isActiveForTomorrowDelivery: row.isActiveForTomorrowDelivery,
            isActiveForExpressDelivery: row.isActiveForExpressDelivery,
            isActiveForTodayDelivery: row.isActiveForTodayDelivery,
            addressEntityId: row.addressEntityId,
        });
        setEditDeliveryDrawerOpen(true);
    };

    const handleFilter = async () => {
        try {
            setLoading(true);
            const shippingCompanyList =
                companyId === 0
                    ? companyList.filter((company: Company) => company.value !== 0).map((company) => company.value)
                    : [companyId];

            await FastDeliveryCargoManagementQuery.GetFastDeliveryCargo({
                shippingCompanyList: shippingCompanyList,
                deliveryIdList: [],
            }).then((res: any) => {
                if (res.model.fastCargoManagementList) {
                    const formattedData = res.model.fastCargoManagementList.map((item: any) => ({
                        ...item,
                        isActiveForTomorrowDelivery: item.isActiveForTomorrowDelivery ? 1 : 0,
                        isActiveForExpressDelivery: item.isActiveForExpressDelivery ? 1 : 0,
                        isActiveForTodayDelivery: item.isActiveForTodayDelivery ? 1 : 0,
                    }));
                    setGridData(formattedData);
                    setExcelHiddenColumns(res.model.hiddenExcelFields);
                } else {
                    Swal.fire('Service Error', res.errorMessage, 'error');
                }
            });
        } catch (error: any) {
            Swal.fire('Error', error.toString(), 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async (file: File) => {
        try {
            setBulkUploadResult([]);
            setLoading(true);
            const formData = new FormData();
            formData.append('file', file, file.name);

            const response = await FastDeliveryCargoManagementCommand.BulkUploadFastDeliveryCargo(formData);
            if (response) {
                if (response.model && Array.isArray(response.model.responseItems)) {
                    setBulkUploadResult(
                        response.model.responseItems.map((item: any, idx: number) => ({
                            shippingCompanyName:
                                companyList.find((c) => c.value === item.shippingCompanyId)?.text ||
                                item.shippingCompanyId,
                            ...item,
                            id: idx + 1,
                            errorMessage: item.errorMessage,
                            isActiveForTomorrowDelivery: item.isActiveForTomorrowDelivery ? 1 : 0,
                            isActiveForExpressDelivery: item.isActiveForExpressDelivery ? 1 : 0,
                            isActiveForTodayDelivery: item.isActiveForTodayDelivery ? 1 : 0,
                        }))
                    );
                } else {
                    setBulkUploadResult([]);
                }
                Swal.fire({
                    icon: 'success',
                    title: t('general.success'),
                    text:
                        t('fastDeliveryCargoManagement.bulkUploadSuccess') +
                        ', ' +
                        t('fastDeliveryCargoManagement.mismatchedDataWillBeShown'),
                    showConfirmButton: false,
                    timer: 1500,
                });
                await handleFilter();
            }
        } catch (error) {
            console.error('Dosya yükleme hatası:', error);
            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: t('fastDeliveryCargoManagement.bulkUploadError'),
                confirmButtonText: t('general.ok'),
            });
        } finally {
            setLoading(false);
        }
    };

    const bulkUploadColumns: ExtendedGridColDef[] = [
        {
            field: 'shippingCompanyName',
            headerName: t('fastDeliveryCargoManagement.shippingCompany'),
            flex: 1,
            minWidth: 150,
        },
        { field: 'cityName', headerName: t('fastDeliveryCargoManagement.city'), flex: 1, minWidth: 120 },
        { field: 'countyName', headerName: t('fastDeliveryCargoManagement.district'), flex: 1, minWidth: 120 },
        {
            field: 'neighborhoodName',
            headerName: t('fastDeliveryCargoManagement.neighborhood'),
            flex: 1,
            minWidth: 120,
        },
        {
            field: 'isActiveForTomorrowDelivery',
            headerName: t('fastDeliveryCargoManagement.tomorrowDelivery'),
            flex: 1,
            minWidth: 120,
            valueGetter: (params: any) => (params ? 1 : 0),
        },
        {
            field: 'isActiveForExpressDelivery',
            headerName: t('fastDeliveryCargoManagement.expressDelivery'),
            flex: 1,
            minWidth: 120,
            valueGetter: (params: any) => (params ? 1 : 0),
        },
        {
            field: 'isActiveForTodayDelivery',
            headerName: t('fastDeliveryCargoManagement.todayDelivery'),
            flex: 1,
            minWidth: 120,
            valueGetter: (params: any) => (params ? 1 : 0),
        },
        {
            field: 'errorMessage',
            headerName: t('fastDeliveryCargoManagement.errorMessage'),
            flex: 1,
            minWidth: 180,
            renderCell: (params) =>
                params.value ? <span style={{ color: 'red' }}>{params.value}</span> : params.value,
        },
    ];

    useEffect(() => {
        GetCargoCompanyList();
    }, []);

    const renderContent = () => {
        if (!gridData || gridData.length === 0) {
            return (
                <Box
                    className={FDCM.emptyStateWrap}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '400px',
                    }}
                >
                    <SCUINoRecordComponent />
                    <FormControl className={FDCM.shippingCompanyFieldWrap} sx={{ width: 450, margin: '10px' }}>
                        <InputLabel id="labelApp">{t('fastDeliveryCargoManagement.shippingCompany')}</InputLabel>
                        <Select
                            className={FDCM.inputSelectShippingCompany}
                            label={t('fastDeliveryCargoManagement.shippingCompany')}
                            labelId="labelApp"
                            id="parentMenuLabel"
                            value={companyId}
                            onChange={(event) => {
                                setCompanyId(Number(event.target.value));
                            }}
                            fullWidth
                        >
                            {companyList &&
                                companyList.map((comp: any) => (
                                    <MenuItem key={comp.value} id={comp.value.toString()} value={comp.value}>
                                        {comp.text}
                                    </MenuItem>
                                ))}
                        </Select>

                        <SCUIButton
                            className={FDCM.inputButtonFilter}
                            text={t('general.filter')}
                            type="primary"
                            sizeType="default"
                            size={'l'}
                            onClick={handleFilter}
                            style={{ marginTop: '10px' }}
                        />
                    </FormControl>
                </Box>
            );
        }

        return (
            <>
                <TabContainer selectedTab={selectedTab} onTabChange={handleTabChange} />

                {selectedTab === 0 ? (
                    gridData &&
                    gridData.length > 0 && (
                        <>
                            <Box className={FDCM.listToolbarWrap} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 2 }}>
                                <SCUIButton
                                    className={FDCM.inputButtonAddNew}
                                    text={t('fastDeliveryCargoManagement.addNew')}
                                    type="primary"
                                    sizeType="default"
                                    size="s"
                                    onClick={() => setAddDeliveryDrawerOpen(true)}
                                />
                                <Box className={FDCM.exportExcelWrap}>
                                    <SCUIDownloadButton
                                        position="right"
                                        containerStyle={{ marginLeft: '10px' }}
                                        excelData={{
                                            excelDownloadData: gridData,
                                            excelDownloadFolderName: t('fastDeliveryCargoManagement.title'),
                                            excelDownloadButtonText: t('fastDeliveryCargoManagement.exportExcel'),
                                            hiddenExcelColumn: excelHiddenColumns,
                                        }}
                                    />
                                </Box>
                            </Box>
                            <DeliveryListTable rows={gridData} onEdit={handleEdit} columns={columns} />
                        </>
                    )
                ) : (
                    <>
                        <BulkUploadTab onUpload={handleFileUpload} loading={loading} />
                        {bulkUploadResult.length > 0 && (
                            <Box className={FDCM.bulkResultsWrap} sx={{ mt: 4 }}>
                                <Box className={FDCM.bulkExportWrap} sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                    <SCUIDownloadButton
                                        position="right"
                                        excelData={{
                                            excelDownloadData: bulkUploadResult,
                                            excelDownloadFolderName: t(
                                                'fastDeliveryCargoManagement.exportMismatchedDataTitle'
                                            ),
                                            excelDownloadButtonText: t(
                                                'fastDeliveryCargoManagement.exportMismatchedData'
                                            ),
                                            hiddenExcelColumn: [
                                                'id',
                                                'entityId',
                                                'shippingCompanyId',
                                                'addressEntityId',
                                                'isSuccess',
                                                'errorMessage',
                                                'matchedAddressEntityId',
                                            ],
                                        }}
                                    />
                                </Box>
                                <Box className={FDCM.bulkResultsGridWrap}>
                                    <SCUICustomDataGrid
                                        rows={bulkUploadResult}
                                        columns={bulkUploadColumns}
                                        excelData={{
                                            excelDownloadData: bulkUploadResult,
                                            excelDownloadButtonText: t(
                                                'fastDeliveryCargoManagement.exportMismatchedData'
                                            ),
                                        }}
                                        autoHeight
                                        hideFooter
                                    />
                                </Box>
                            </Box>
                        )}
                    </>
                )}
            </>
        );
    };

    return (
        <SCUIContent>
            <Box className={FDCM.page}>
                <EditDeliveryDrawer
                    open={editDeliveryDrawerOpen}
                    onClose={() => setEditDeliveryDrawerOpen(false)}
                    delivery={editDeliveryRow}
                    onSave={handleSave}
                    loading={loading}
                />
                <AddDeliveryDrawer
                    open={addDeliveryDrawerOpen}
                    onClose={() => {
                        setAddDeliveryDrawerOpen(false);
                    }}
                    onSave={handleAdd}
                    loading={loading}
                    companyList={companyList}
                    key={addDeliveryDrawerOpen ? 'open' : 'closed'}
                />
                <Grid className={FDCM.mainGrid} container direction="row" spacing={2}>
                    <Grid
                        size={{
                            sm: 12,
                            md: 12,
                            lg: 12,
                            xl: 12,
                        }}
                    >
                        <div className={FDCM.breadcrumbSection}>
                            <Grid
                                className={FDCM.breadcrumbWrap}
                                container
                                direction="row"
                                spacing={2}
                                sx={{ marginLeft: '0.5rem' }}
                            >
                                <SCUIBreadCrumb
                                    title={t('fastDeliveryCargoManagement.title')}
                                    componentLink={'/fast-delivery-cargo-management'}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => {
                                        setGridData([]);
                                        setSelectedTab(0);
                                    }}
                                />
                                {loading && (
                                    <Grid
                                        className={FDCM.loadingSection}
                                        size={{
                                            xl: 12,
                                            md: 12,
                                            lg: 12,
                                            xs: 12,
                                        }}
                                    >
                                        <SCUILoadingLinear display={loading} />
                                    </Grid>
                                )}
                            </Grid>
                        </div>
                        <div className={FDCM.contentSection}>{renderContent()}</div>
                    </Grid>
                </Grid>
            </Box>
        </SCUIContent>
    );
};

export default FastDeliveryCargoManagement;
